import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { useOrgSettings, type InvoiceRow, type PeriodRow } from "../../../data/queries";
import { useOrgConfig } from "../../../model/org-config";
import { viewInvoice, type InvoiceView, holidayRegionOf } from "../../../model/invoice-view";
import { today as todayIn } from "../../../lib/format";
import { ladeFormularDaten, ladeKooperation, ladeKooperationen } from "./daten";

/** Abfragen der Gruppe. Schlüssel beginnen mit der Tabelle, damit das Neuladen nach Aktionen greift. */

export function useKooperationen() {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  return useQuery({ queryKey: ["deals", orgId, "kooperationen"], queryFn: () => ladeKooperationen(api, orgId) });
}

export function useKooperation(dealId: string | undefined) {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const payout = ws.can("trigger_payouts");
  return useQuery({
    queryKey: ["deals", orgId, "kooperation", dealId, payout],
    enabled: !!dealId,
    queryFn: () => ladeKooperation(api, orgId, dealId!, { auszahlungLesbar: payout }),
  });
}

export function useFormularDaten() {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  return useQuery({ queryKey: ["talents", orgId, "kooperation-formular"], queryFn: () => ladeFormularDaten(api, orgId) });
}

export function useHeute(): string {
  const ws = useStaffWorkspace();
  return todayIn(ws.org.timezone);
}

/**
 * Rechnungen mit Statuspille und nächstem Schritt wie auf der Seite „Rechnungen“ (model/invoice-view).
 * Ohne vollständige Einstellungen der Agentur (Fachlogik-Konfiguration) gibt es keine Pille, dann zeigt die Seite den Rohstatus.
 */
export function useRechnungsAnsicht(periods: readonly PeriodRow[] | undefined, invoices?: readonly InvoiceRow[]) {
  const ws = useStaffWorkspace();
  const cfg = useOrgConfig(ws.org.org_id);
  const settings = useOrgSettings(ws.org.org_id);
  const today = todayIn(ws.org.timezone);
  // Fälligkeit der Auszahlung je offener Creator-Rechnung wie auf der Seite „Rechnungen“ (PRAXIS-R3-3): im Modus „nach Zahlung der
  // Marke“ erst ab ihrer Zahlung; ohne diese Angabe galt die Frist der Rechnung und die Seite meldete „Auszahlung überfällig“
  const ids = payoutInfoIds(invoices ?? []);
  const info = useQuery({
    queryKey: ["invoice_payout_info", ws.org.org_id, "kooperationen", ids.join(",")],
    enabled: ids.length > 0,
    queryFn: () => api.read<{ invoice_id: string; payout_due?: string | null }[]>("invoice_payout_info", { p_invoices: ids }).catch(() => []),
  });
  return useMemo(() => {
    const map = new Map((periods ?? []).map((p) => [p.id, p]));
    const payoutDue = info.data ? new Map(info.data.map((p) => [p.invoice_id, p.payout_due ?? null])) : undefined;
    return (row: InvoiceRow): InvoiceView | null => {
      if (!cfg.data) return null;
      return viewInvoice(row, { cfg: cfg.data, today, holidayRegion: holidayRegionOf(settings.data), periods: map, ...(payoutDue ? { payoutDue } : {}) });
    };
  }, [cfg.data, settings.data, periods, today, info.data]);
}

/** Offene Creator-Rechnungen an die Agentur, deren Auszahlungstermin invoice_payout_info liefert (sortiert, für den Abfrageschlüssel). */
export function payoutInfoIds(invoices: readonly Pick<InvoiceRow, "id" | "kind" | "status">[]): string[] {
  return invoices.filter((i) => i.kind === "talent_to_agency" && (i.status === "approved" || i.status === "sent")).map((i) => i.id).sort();
}
